// ── EASY-TO-EDIT CHART SETTINGS ─────────────────────────────────────────────
// Bloc colours live in `$lib/constants/colors.ts`, shared with `crosswalk.ts` —
// edit there to recolour this chart and legend (or any other chart in the
// project). If dropping this component into a project without that shared
// file, replace this import with a local `chartColors` object instead.
import { blocColors as chartColors } from '$lib/constants/colors';

// mode: 'fit' labels parties when space permits; 'all' attempts every label;
// 'selected' labels only parties named in show. Entries in hide always win.
export const labelSettings = {
  mode: 'fit',
  minSeats: 3,
  mobileMinSeats: 7,
  mobileBreakpoint: 600,
  maxCharacters: 18,
  pixelsPerCharacter: 5.8,
  show: [],
  hide: [],
  aliases: {
    'Labor Party': 'Labour',
    'United Torah Judaism': 'UTJ',
    'National Religious Party': 'NRP',
    'Yisrael Beitenu': 'IOH',
    'Yisrael Ba-Aliya': 'YBA',
    'The Jewish Home': 'Jewish Home',
    'Religious Zionism': 'RZ',
    'National Unity Party': 'National Unity',
    "Hadash-Ta'al": 'Hadash–Ta’al',
    "Ra'am": 'Ra’am',
  },
  // Full-name overrides for tooltips — swaps a transliterated historical CSV
  // name for the English name used elsewhere on the page (crosswalk.ts,
  // rngs.io content), without touching the raw CSV name every bloc/family/
  // coalition lookup in this file keys off.
  fullNameOverrides: {
    'Yisrael Beitenu': 'Israel Our Home',
  },
};

// Set show to false for a plain stacked-seat chart without continuity bands.
export const connectionSettings = {
  show: true,
  opacity: 0.38,
};

// Black outlines on the latest election row. The 2022 Religious Zionism list
// included Otzma Yehudit and Noam, so all three are represented by that one
// election-result rectangle. Review this dated snapshot before publication.
export const coalitionSettings = {
  show: true,
  electionDate: '2022-11-01',
  parties: ['Likud', 'Religious Zionism', 'Shas', 'United Torah Judaism'],
  stroke: '#111111',
  strokeWidth: 2.5,
};

// Dashed outline + label marking a bloc grouping below a specific election
// row — used by ElectionComparison to flag the opposition on the polling
// estimate row. Off by default; the consuming chart supplies `electionDate`
// dynamically since the estimate's date isn't known ahead of time.
export const oppositionSettings = {
  show: false,
  electionDate: null,
  blocs: ['arab', 'left', 'centre', 'right'],
  label: 'Anti-Netanyahu parties',
  stroke: '#666666',
  strokeWidth: 2.5,
  dash: '4 3',
};

// Optional notes shown after bold election labels. Use exact ISO dates so the
// two 2019 elections can be annotated independently. Values render unbolded.
export const electionAnnotations = {
  '1996-05-29': 'Netanyahu is elected PM',
  // '2022-11-01': 'Add another note here',
};

// Parties listed under the same key get a connecting band between adjacent
// elections. Keys are internal IDs; values must exactly match the CSV names.
// A party can currently belong to one visual family only. Unlisted parties
// use their own party name and will connect only to an identical later name.
const partyFamilies = {
  labour: [
    'Labor Party',
    'One Israel',
    'Labor-Meimad',
    'Zionist Union',
    'Labor-Gesher',
    'Labor-Gesher-Meretz',
    // Current-campaign party (not in the historical CSV), bucketed here so
    // it connects to its 2022 predecessor on the Election Comparison chart.
    'Democrats',
  ],
  meretz: ['Meretz', 'Democratic Camp'],
  likud: ['Likud', 'Likud-Gesher-Tzomet', 'Likud Beitenu'],
  yeshAtid: [
    'Yesh Atid',
    'Blue and White',
    // Current-campaign parties (not in the historical CSV), bucketed here so
    // they connect to their 2022 predecessor on the Election Comparison chart.
    'Together',
    'Yashar',
  ],
  blueWhite: ['National Unity Party'],
  nationalReligious: [
    'National Religious Party',
    'National Union-NRP',
    'The Jewish Home',
    'Union of Right-Wing Parties',
    'Yamina',
  ],
  nationalUnion: ['National Union', 'Religious Zionism'],
  jointList: [
    'Hadash',
    'Balad',
    "Ra'am",
    "Ra'am-Ta'al",
    'Hadash-Balad',
    "Mada-Ra'am",
    'The Joint List',
    "Hadash-Ta'al",
    "Ra'am-Balad",
    "Joint List (Hadash, Ra'am, Ta'al, Balad)",
    // Current-campaign combined-list label (not in the historical CSV),
    // bucketed here so it connects to 2022's Ra'am/Hadash-Ta'al on the
    // Election Comparison chart.
    'Joint List',
  ],
};

// Shared by both bloc sets below: resolves each party's connecting-band
// `family` (see `partyFamilies` above) and display `short`/`fullName` the
// same way, regardless of which bloc grouping is in effect. A definitions
// entry is normally just a party name string; pass `{ name, weight }`
// instead to break ties within the same bloc — parties otherwise sort by
// family name alphabetically, which can accidentally sandwich a party
// between two members of a curated `oppositionSettings`/`coalitionSettings`
// outline (see Likud below). Higher weight sorts later within the bloc.
const buildPartyMeta = (definitions) =>
  Object.fromEntries(
    Object.entries(definitions).flatMap(([bloc, parties]) =>
      parties.map((entry) => {
        const party = typeof entry === 'string' ? entry : entry.name;
        const weight = typeof entry === 'string' ? 0 : entry.weight || 0;
        const family =
          Object.entries(partyFamilies).find(([, members]) =>
            members.includes(party)
          )?.[0] || party;
        return [
          party,
          {
            bloc,
            family,
            weight,
            short: labelSettings.aliases[party] || party,
            fullName: labelSettings.fullNameOverrides[party] || party,
          },
        ];
      })
    )
  );

// ── Default bloc set — the standalone Historical Elections timeline (2009 on) ──
// Right, Likud, far-right and national-religious are merged into one "Right
// wing parties" bloc/colour here. `ElectionComparison`'s 2022-vs-2026 chart
// keeps those four apart — see `comparisonBlocMeta`/`comparisonPartyMeta`
// further down, which it passes explicitly instead of using these defaults.
export const blocMeta = {
  arab: { label: 'Arab', color: chartColors.arab, order: 0 },
  left: { label: 'Left', color: chartColors.left, order: 1 },
  centre: { label: 'Centre', color: chartColors.centre, order: 2 },
  right: { label: 'Right', color: chartColors.rightBloc, order: 3 },
  // Only ever applied via the 2022 `partyNotes` override below, so that row's
  // Religious Zionism/Jewish Power segment reads as far-right here too,
  // matching `comparisonBlocMeta` in the Election Comparison chart, instead
  // of blending into the merged "Right" bloc used for every other election.
  farRight: { label: 'Far-right', color: chartColors.farRight, order: 4 },
  ultraOrthodox: {
    label: 'Ultra-Orthodox',
    color: chartColors.ultraOrthodox,
    order: 5,
  },
  other: { label: 'Other parties', color: chartColors.other, order: 6 },
};

// Editorial metadata, not part of the election-results CSV. Review these labels
// with a subject-matter editor before publication.
const definitions = {
  arab: [
    'Hadash',
    'Arab Democratic Party',
    'Hadash-Balad',
    "Mada-Ra'am",
    "Ra'am",
    "Ra'am-Ta'al",
    'Balad',
    'The Joint List',
    'Joint List (Hadash, Ra’am, Ta’al, Balad)',
    "Joint List (Hadash, Ra'am, Ta'al, Balad)",
    "Hadash-Ta'al",
    "Ra'am-Balad",
  ],
  left: [
    'Labor Party',
    'Meretz',
    'One Israel',
    'Labor-Meimad',
    'Am Ehad',
    'Zionist Union',
    'Labor-Gesher',
    'Democratic Camp',
    'Labor-Gesher-Meretz',
  ],
  centre: [
    'Third Way',
    'Shinui',
    'Center Party',
    'Kadima',
    'Yesh Atid',
    'Blue and White',
    'Kulanu',
    'Gil',
    // Moved here from `right` — Hatnua (Tzipi Livni, 2013) and National
    // Unity Party (Benny Gantz, 2022) are centrist, not right-wing.
    'Hatnua',
    'National Unity Party',
  ],
  right: [
    // Formerly split across separate right/Likud/far-right/national-religious
    // blocs; merged into one "Right wing parties" bloc/colour for this
    // timeline (kept apart for `comparisonDefinitions` below).
    'Yisrael Ba-Aliya',
    'Yisrael Beitenu',
    'New Hope',
    'Likud',
    'Likud-Gesher-Tzomet',
    'Likud Beitenu',
    'Tzomet',
    'Moledet',
    'National Union',
    'Yamina',
    'Religious Zionism',
    'Union of Right-Wing Parties',
    'National Religious Party',
    'National Union-NRP',
    'The Jewish Home',
  ],
  ultraOrthodox: ['Shas', 'United Torah Judaism'],
};

export const partyMeta = buildPartyMeta(definitions);

// Election-specific label/tooltip overrides, keyed by election date then
// exact party name — for when a party's chart label or tooltip needs to
// differ from its default for one specific election (e.g. a list that ran
// with an ally that year). Used only by the standalone Historical Elections
// timeline; `ElectionComparison` passes its own single-entry override for
// the 2022 Religious Zionism label instead of reusing this object, since its
// bloc set already keeps Religious Zionism in `farRight` every year.
//
// Religious Zionism's 2022 list actually included Otzma Yehudit (Jewish
// Power) and Noam; in 2021 Smotrich's Religious Zionism and Otzma Yehudit
// ran as separate lists (Otzma Yehudit missed the threshold). Both rows are
// labelled "RZ & JP" here to read as one continuing right-wing alliance
// across the two elections — the tooltip spells out the actual list makeup
// for each year.
export const partyNotes = {
  '2021-03-23': {
    'Religious Zionism': {
      short: 'RZ & JP',
      // note: 'Ran separately from Jewish Power, which missed the threshold.',
    },
  },
  '2022-11-01': {
    'Religious Zionism': {
      // Colours this row's segment with the `farRight` bloc above instead of
      // the merged "Right" bloc every other election uses, matching how
      // `comparisonBlocMeta` colours Religious Zionism in ElectionComparison.
      bloc: 'farRight',
      short: 'RZ & JP',
      // note: 'List included Jewish Power (Otzma Yehudit) and Noam.',
    },
    // Sorts Likud after the rest of the "Right" bloc (incl. Yisrael
    // Beitenu) for this election only — otherwise Yisrael Beitenu, which
    // isn't a coalition member, sorts between Likud and Religious
    // Zionism/Shas/UTJ and gets visually swept into the governing-coalition
    // outline below. Mirrors the same fix already applied to Likud in
    // `comparisonDefinitions` further down, which keeps Yisrael Beitenu out
    // of the anti-Netanyahu outline on the 2026 row.
    Likud: { weight: 1 },
  },
};

// ── Comparison-only bloc set — ElectionComparison's 2022-vs-2026 chart ──
// Preserves a three-way split (right/far-right/national-religious) so that
// chart is unaffected by the merged `blocMeta`/`partyMeta` above. Likud no
// longer gets its own bloc/colour here — it's folded into `right`.
export const comparisonBlocMeta = {
  arab: { label: 'Arab', color: chartColors.arab, order: 0 },
  left: { label: 'Left', color: chartColors.left, order: 1 },
  centre: { label: 'Centre', color: chartColors.centre, order: 2 },
  right: { label: 'Right', color: chartColors.rightBloc, order: 3 },
  farRight: { label: 'Far-right', color: chartColors.farRight, order: 4 },
  nationalReligious: {
    label: 'National religious',
    color: chartColors.nationalReligious,
    order: 5,
  },
  ultraOrthodox: {
    label: 'Ultra-Orthodox',
    color: chartColors.ultraOrthodox,
    order: 6,
  },
  other: { label: 'Other parties', color: chartColors.other, order: 7 },
  // Chart-only filler for seats an estimate row can't attribute to a party —
  // see `fillRemainder` in `ElectionFlow.svelte`. Shares `right`'s order so
  // the two blocs sort as one group by weight (see the remainder entry's
  // `weight: 0.5` in `ElectionFlow.svelte`'s `prepare()`), landing the gray
  // segment right after Yisrael Beitenu (weight 0) but before Likud (weight
  // 1) — always ahead of Likud, and outside the anti-Netanyahu outline
  // (whose bounds stop at Yisrael Beitenu, its last curated member).
  unassigned: { label: 'Unassigned', color: chartColors.unassigned, order: 3 },
};

const comparisonDefinitions = {
  arab: [
    'Hadash',
    'Arab Democratic Party',
    'Hadash-Balad',
    "Mada-Ra'am",
    "Ra'am",
    "Ra'am-Ta'al",
    'Balad',
    'The Joint List',
    'Joint List (Hadash, Ra’am, Ta’al, Balad)',
    "Joint List (Hadash, Ra'am, Ta'al, Balad)",
    "Hadash-Ta'al",
    "Ra'am-Balad",
    // Current-campaign party label from `crosswalk.ts` (see note below).
    'Joint List',
  ],
  left: [
    'Labor Party',
    'Meretz',
    'One Israel',
    'Labor-Meimad',
    'Am Ehad',
    'Zionist Union',
    'Labor-Gesher',
    'Democratic Camp',
    'Labor-Gesher-Meretz',
    // Current-campaign party label from `crosswalk.ts` (see note on Joint
    // List/Reservists/People of Israel above/below for why this is bucketed
    // by its own label rather than mapped to a 2022 predecessor name).
    'Democrats',
  ],
  centre: [
    'Third Way',
    'Shinui',
    'Center Party',
    'Kadima',
    'Yesh Atid',
    'Blue and White',
    'Kulanu',
    'Gil',
    // Current-campaign parties (not in the historical CSV), bucketed here so
    // the Election Comparison chart's polling-estimate row colours them right.
    'Together',
    'Yashar',
  ],
  right: [
    'Yisrael Ba-Aliya',
    'Yisrael Beitenu',
    'New Hope',
    'National Unity Party',
    'Hatnua',
    // Reservists and People of Israel are deliberately NOT listed anywhere in
    // this object — see the `unassigned` entry below, which they don't even
    // appear under by name: `ElectionComparison/index.svelte` excludes both
    // from the chart entirely and instead sizes that entry's row as whatever
    // seats are left to reach 120, until more polling clarifies which bloc
    // they actually belong to.
    // Folded in from the former standalone `likud` bloc — Likud no longer
    // gets its own colour on this chart, it's just part of Right. Weighted
    // to sort after the rest of Right (incl. Yisrael Beitenu, an
    // `antiNetanyahuBloc` member) so it can't end up sandwiched inside that
    // outline's min/max bounding box on the 2026 row.
    { name: 'Likud', weight: 1 },
    { name: 'Likud-Gesher-Tzomet', weight: 1 },
    { name: 'Likud Beitenu', weight: 1 },
  ],
  farRight: [
    'Tzomet',
    'Moledet',
    'National Union',
    'Yamina',
    'Religious Zionism',
    'Union of Right-Wing Parties',
    // Current-campaign party, see note above.
    'Jewish Power',
  ],
  nationalReligious: [
    'National Religious Party',
    'National Union-NRP',
    'The Jewish Home',
  ],
  ultraOrthodox: ['Shas', 'United Torah Judaism'],
  // TEMPORARY, comparison chart only — Reservists and People of Israel have
  // no confirmed real-world bloc yet, so they're excluded from this chart
  // entirely rather than plotted under their own names. This single "Other
  // parties" entry instead represents whatever's left to reach 120 seats
  // after every other displayed party (see `ElectionComparison/index.svelte`'s
  // `estimateRows`) — reconciling per-party trend estimates that don't
  // naturally sum to 120 on their own, not a literal Reservists+People of
  // Israel total. Coloured with the same gray "unassigned" filler bloc as
  // leftover/uncounted seats (see `fillRemainder` in `ElectionFlow.svelte`)
  // rather than a distinct legend entry; the existing `unassignedNote` copy
  // already explains the gray band, and the tooltip reads "Other parties"
  // via this entry's `short`/`fullName`.
  // To revert: delete this entry and the reconciliation step in
  // `ElectionComparison/index.svelte` (parties drop the `minSeatsToShow`
  // threshold and stop reconciling to 120) or move Reservists/People of
  // Israel's names up into a real bloc above, e.g. `right`.
  unassigned: [{ name: 'Other parties', weight: 0.5 }],
};

export const comparisonPartyMeta = buildPartyMeta(comparisonDefinitions);
